import { Type } from '@sinclair/typebox';
import { EmailSchema, IsoDateTimeSchema, LocaleSchema, PasswordSchema, TotpCodeSchema, UuidSchema } from './primitives.js';
import { literalUnion } from './union.js';
/**
 * Contracts for the identity application, mounted at `/identity`.
 *
 * Two rules run through all of it:
 *
 *  - **Nothing leaks whether an account exists.** Signup, password reset and
 *    resend all answer 202 with the same body whatever the truth is. An
 *    endpoint that returned "email already registered" would turn this API
 *    into a membership oracle for anyone with a word list.
 *  - **Two factors, always.** A password alone never produces a session; it
 *    produces a challenge. The only things that close a challenge are a TOTP
 *    code or a recovery code.
 */
export const APP_CODES = ['identity', 'chat', 'photos'];
export const AppCodeSchema = literalUnion(APP_CODES);
/**
 * Where an account is in its life. `deleting` is the hour between asking for
 * the account to be deleted and it being erased, during which it holds no
 * session and cannot be signed into - see {@link ACCOUNT_DELETION_GRACE_MINUTES}.
 *
 * `deleting` was added before any build of the Android app was released, which
 * is the only moment it could be: the app reads this set in every sign-in and
 * refresh it makes, and a value an installed build has never seen is a break
 * the API-surface guard refuses once a build is recorded.
 */
export const UserStatusSchema = Type.Union([Type.Literal('pending_verification'), Type.Literal('active'), Type.Literal('locked'), Type.Literal('deleting')]);
// ---------------------------------------------------------------------------
// Shared representations
// ---------------------------------------------------------------------------
/** Everything the client may know about the signed-in user. Never a hash, never a secret. */
export const PublicUserSchema = Type.Object({
    id: UuidSchema,
    email: EmailSchema,
    locale: LocaleSchema,
    status: UserStatusSchema,
    isAdmin: Type.Boolean(),
    emailVerifiedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
});
export const PlanSummarySchema = Type.Object({
    code: Type.String(),
    name: Type.String(),
    limits: Type.Record(Type.String(), Type.Number()),
    expiresAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
});
export const UsageSchema = Type.Record(Type.String(), Type.Number());
/** One row of the "your devices" list. */
export const SessionSummarySchema = Type.Object({
    id: UuidSchema,
    clientKind: Type.Union([Type.Literal('web'), Type.Literal('native')]),
    deviceLabel: Type.Union([Type.String(), Type.Null()]),
    createdAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
    /** True for the session making this request, so the UI can label it "this device". */
    current: Type.Boolean(),
});
/**
 * Access tokens are returned in the body for native clients and are ALSO the
 * value web clients hold in memory. The refresh token is never in a body for
 * web - it is set as an httpOnly cookie the JavaScript cannot read.
 */
export const AccessTokenSchema = Type.Object({
    accessToken: Type.String(),
    expiresIn: Type.Integer(),
    /** Present only for native clients; web clients get the httpOnly cookie instead. */
    refreshToken: Type.Optional(Type.String()),
});
export const SessionStartedSchema = Type.Intersect([
    AccessTokenSchema,
    Type.Object({
        user: PublicUserSchema,
        grants: Type.Array(AppCodeSchema),
    }),
]);
/** Web or native decides cookie-versus-body for the refresh token. */
export const ClientKindValueSchema = Type.Union([Type.Literal('web'), Type.Literal('native')], { default: 'web' });
/** Optional on the wire: a client that says nothing is a browser. */
export const ClientKindSchema = Type.Optional(ClientKindValueSchema);
// ---------------------------------------------------------------------------
// Signup and verification
// ---------------------------------------------------------------------------
export const SignupBodySchema = Type.Object({
    email: EmailSchema,
    password: PasswordSchema,
    locale: Type.Optional(LocaleSchema),
});
/**
 * Deliberately empty of information. The same 202 comes back whether the
 * address was free, already registered, or already registered and verified -
 * only the email that gets sent differs.
 */
export const AcceptedSchema = Type.Object({ accepted: Type.Literal(true) });
export const VerifyEmailBodySchema = Type.Object({ token: Type.String({ minLength: 16, maxLength: 512 }) });
/**
 * Verifying an address does not sign the user in. It returns a short-lived
 * enrolment ticket, because 2FA is mandatory and the account is not usable
 * until a TOTP secret is confirmed.
 */
export const VerifyEmailResponseSchema = Type.Object({
    enrolmentToken: Type.String(),
    expiresIn: Type.Integer(),
});
export const ResendVerificationBodySchema = Type.Object({ email: EmailSchema });
// ---------------------------------------------------------------------------
// TOTP enrolment
// ---------------------------------------------------------------------------
export const TotpStartBodySchema = Type.Object({ enrolmentToken: Type.String() });
export const TotpStartResponseSchema = Type.Object({
    /** Base32, shown so the secret can be typed into an authenticator manually. */
    secret: Type.String(),
    /** otpauth:// URI for the QR code. Rendered client-side - never round-trip an image. */
    otpauthUri: Type.String(),
});
export const TotpConfirmBodySchema = Type.Object({
    enrolmentToken: Type.String(),
    code: TotpCodeSchema,
});
/**
 * The only time recovery codes are ever transmitted. They are stored hashed,
 * so this response cannot be reproduced - the UI must make the user save them.
 */
export const TotpConfirmResponseSchema = Type.Object({ recoveryCodes: Type.Array(Type.String()) });
// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
export const LoginBodySchema = Type.Object({
    email: EmailSchema,
    password: Type.String({ minLength: 1, maxLength: 256 }),
    clientKind: ClientKindSchema,
});
/**
 * A correct password yields only this. `challengeToken` is short-lived and
 * signed; it proves the first factor passed and nothing more.
 */
export const LoginChallengeSchema = Type.Object({
    challengeToken: Type.String(),
    expiresIn: Type.Integer(),
});
export const TotpChallengeBodySchema = Type.Object({
    challengeToken: Type.String(),
    code: TotpCodeSchema,
    deviceLabel: Type.Optional(Type.String({ maxLength: 120 })),
});
export const RecoveryChallengeBodySchema = Type.Object({
    challengeToken: Type.String(),
    recoveryCode: Type.String({ minLength: 8, maxLength: 64 }),
    deviceLabel: Type.Optional(Type.String({ maxLength: 120 })),
});
// ---------------------------------------------------------------------------
// Session lifecycle
// ---------------------------------------------------------------------------
/** Web sends nothing - the cookie carries it. Native must send the token. */
export const RefreshBodySchema = Type.Object({ refreshToken: Type.Optional(Type.String()) });
export const LogoutBodySchema = Type.Object({
    /** When true, ends every session on every device rather than just this one. */
    allDevices: Type.Optional(Type.Boolean({ default: false })),
});
// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------
export const ForgotPasswordBodySchema = Type.Object({ email: EmailSchema });
export const ResetPasswordBodySchema = Type.Object({
    token: Type.String({ minLength: 16, maxLength: 512 }),
    password: PasswordSchema,
});
export const ChangePasswordBodySchema = Type.Object({
    currentPassword: Type.String({ minLength: 1, maxLength: 256 }),
    newPassword: PasswordSchema,
});
// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
export const MeResponseSchema = Type.Object({
    user: PublicUserSchema,
    plan: PlanSummarySchema,
    usage: UsageSchema,
    grants: Type.Array(AppCodeSchema),
});
export const UpdateMeBodySchema = Type.Object({ locale: Type.Optional(LocaleSchema) });
export const SessionListResponseSchema = Type.Object({ sessions: Type.Array(SessionSummarySchema) });
// ---------------------------------------------------------------------------
// Account deletion
// ---------------------------------------------------------------------------
/**
 * How long a deletion can be taken back, in minutes, and so how long after the
 * request the account is erased.
 *
 * One constant in this package for the reason `TRASH_RETENTION_DAYS` is one:
 * three things in different processes have to agree about it - the request
 * that sets the due time, the email that tells the owner how long they have,
 * and the page that promises it before they press the button.
 */
export const ACCOUNT_DELETION_GRACE_MINUTES = 60;
/**
 * How long a copy of an erased account can survive in a backup, in days: the
 * nightly database dump, and the versions Backblaze keeps of an object after it
 * is deleted.
 *
 * **A promise nothing in this codebase can keep**, which is why it is written
 * down once. The deletion page and the confirmation email both make it; the
 * dump's retention and the bucket's lifecycle rule are what keep it, and the
 * `deploy` skill sets both to this.
 */
export const BACKUP_RETENTION_DAYS = 30;
/**
 * Asking for the signed-in account to be deleted: the password, and a second
 * factor - an authenticator code or one of the recovery codes, exactly one.
 *
 * Both factors again, though the caller already holds a session, because a
 * session is the thing most likely to have been left open somewhere: a tab on a
 * shared computer must not be enough to erase somebody's photographs. The
 * recovery code is accepted because the person whose phone fell in the sea is
 * exactly somebody who may want to leave. "Exactly one" is the service's rule
 * rather than the schema's, so a client that sends neither is told which field
 * is missing rather than that no branch of a union matched.
 */
export const RequestAccountDeletionBodySchema = Type.Object({
    password: Type.String({ minLength: 1, maxLength: 256 }),
    code: Type.Optional(TotpCodeSchema),
    recoveryCode: Type.Optional(Type.String({ minLength: 8, maxLength: 64 })),
});
/**
 * The answer to asking for the account to be deleted: when it will be.
 *
 * Nothing is erased yet. Every session has ended, and the owner has been sent a
 * link that keeps the account until `deleteAt`.
 */
export const AccountDeletionScheduledSchema = Type.Object({ deleteAt: IsoDateTimeSchema });
/** The token from the link in the "your account will be deleted" email. */
export const CancelAccountDeletionBodySchema = Type.Object({ token: Type.String({ minLength: 16, maxLength: 512 }) });
// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------
export const AdminUserSchema = Type.Intersect([
    PublicUserSchema,
    Type.Object({
        planCode: Type.String(),
        usage: UsageSchema,
        grants: Type.Array(AppCodeSchema),
        lastLoginAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    }),
]);
/**
 * Admin search matches on the normalised email prefix. This is NOT the group
 * invitation path - that one has no search at all, by design. This endpoint is
 * admin-only and audited.
 */
export const AdminUserQuerySchema = Type.Object({
    search: Type.Optional(Type.String({ maxLength: 254 })),
    status: Type.Optional(UserStatusSchema),
    cursor: Type.Optional(Type.String({ maxLength: 512 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 50 })),
});
export const AdminUpdateUserBodySchema = Type.Object({
    planCode: Type.Optional(Type.String({ maxLength: 50 })),
    status: Type.Optional(Type.Union([Type.Literal('active'), Type.Literal('locked')])),
    grants: Type.Optional(Type.Array(AppCodeSchema)),
});
// ---------------------------------------------------------------------------
// Quotas
// ---------------------------------------------------------------------------
/**
 * Metric names are a closed set so a typo becomes a compile error rather than
 * a quota that silently never triggers.
 */
export const QUOTA_METRICS = ['storage_bytes', 'media_items', 'albums', 'ai_messages_per_month', 'ai_projects', 'groups'];
/** Monthly metrics reset; the rest accumulate for the life of the account. */
export const MONTHLY_METRICS = ['ai_messages_per_month'];
//# sourceMappingURL=identity.js.map